/**
 * Encodes a string for safe use in URL context
 * 
 * This prevents XSS when embedding user data in:
 * - Query parameters
 * - URL paths
 * - Hash fragments
 * 
 * @param value - The string to encode
 * @returns URL-safe encoded string
 * 
 * @example
 * const userInput = "hello world<script>";
 * const safe = encodeUrl(userInput);
 * // safe = "hello%20world%3Cscript%3E"
 */
export const encodeUrl = (value: string | null | undefined): string => {
  if (!value) {
    return '';
  }

  return encodeURIComponent(String(value));
};

/**
 * Validates and sanitizes a full URL
 * 
 * @param url - The URL to validate
 * @param allowedProtocols - List of allowed protocols (default: ['http:', 'https:'])
 * @returns Validated URL or empty string if invalid
 * 
 * @example
 * const userInput = "javascript:alert('XSS')";
 * const safe = validateUrl(userInput);
 * // safe = "" (blocked)
 * 
 * const userInput2 = "https://example.com";
 * const safe2 = validateUrl(userInput2);
 * // safe2 = "https://example.com" (allowed)
 */
export const validateUrl = (
  url: string | null | undefined,
  allowedProtocols: string[] = ['http:', 'https:', 'mailto:']
): string => {
  if (!url) {
    return '';
  }

  const urlStr = String(url).trim();

  // Block javascript: and data: protocols
  const dangerousProtocols = ['javascript:', 'data:', 'vbscript:', 'file:'];
  const lowerUrl = urlStr.toLowerCase();

  for (const protocol of dangerousProtocols) {
    if (lowerUrl.startsWith(protocol)) {
      return '';
    }
  }

  // Try to parse as URL
  try {
    const parsed = new URL(urlStr);

    // Check if protocol is allowed
    if (!allowedProtocols.includes(parsed.protocol)) {
      return '';
    }

    return parsed.href;
  } catch {
    // If parsing fails, it might be a relative URL
    // Allow relative URLs that don't start with dangerous patterns
    if (
      !lowerUrl.startsWith('javascript:') &&
      !lowerUrl.startsWith('data:') &&
      !lowerUrl.startsWith('vbscript:')
    ) {
      return urlStr;
    }

    return '';
  }
};

/**
 * Encodes a query parameter value for safe use in URLs
 * 
 * @param value - The value to encode
 * @returns URL-encoded parameter value
 * 
 * @example
 * const userInput = "name=John&password=secret";
 * const safe = encodeQueryParam(userInput);
 * // safe = "name%3DJohn%26password%3Dsecret"
 * // Use: ?search=${safe}
 */
export const encodeQueryParam = (value: string | null | undefined): string => {
  return encodeUrl(value);
};

/**
 * Encodes multiple query parameters into a query string
 * 
 * @param params - Object with key-value pairs
 * @returns URL-encoded query string
 * 
 * @example
 * const params = { name: "John Doe", search: "<script>" };
 * const queryString = encodeQueryParams(params);
 * // queryString = "name=John%20Doe&search=%3Cscript%3E"
 */
export const encodeQueryParams = (params: Record<string, any>): string => {
  const pairs: string[] = [];

  for (const key in params) {
    if (Object.prototype.hasOwnProperty.call(params, key)) {
      const value = params[key];
      if (value != null) {
        pairs.push(
          `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`
        );
      }
    }
  }

  return pairs.join('&');
};

